import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { RefreshStatus } from '../components/RefreshStatus';
import { Disclaimer } from '../components/Disclaimer';
import { Skeleton } from '../components/Skeleton';
import { ApiClientError, getFightAutoPicks, getFightTrackRecord } from '../lib/apiClient';
import type { FightSkipReason, UnmatchedFight } from '../lib/fightAutoPicks';
import { netFightEdge } from '../lib/fightMarketEdge';
import type { FightLogSummary } from '../lib/fightResearchLog';
import { ageOn, type FighterStats } from '../lib/fighterStats';
import { UFC_STATS_SOURCE, type UfcCareerStats } from '../lib/ufcStatsCsv';
import { ExternalLinkIcon } from '../components/icons';
import styles from './FightMarketPage.module.css';

const percent = (value: number): string => `${Math.round(value * 100)}%`;
const cents = (value: number): string => `${Math.round(value * 100)}¢`;
/** Signed cents with one decimal and a real minus sign. */
const signedCents = (value: number): string => {
  const c = Math.round(value * 1000) / 10;
  return `${c > 0 ? '+' : c < 0 ? '−' : ''}${Math.abs(c).toFixed(1)}¢`;
};
/** A signed decimal with a real minus sign. */
const signedFixed = (value: number, places: number): string =>
  `${value > 0 ? '+' : value < 0 ? '−' : ''}${Math.abs(value).toFixed(places)}`;
const WEEK_MS = 7 * 24 * 60 * 60_000;
type Range = 'week' | 'month';
type Pick = Awaited<ReturnType<typeof getFightAutoPicks>>['picks'][number];

const SKIP_REASON_LABELS: Record<FightSkipReason, string> = {
  unparseableTitle: "Kalshi's event title didn't split into two fighters",
  unexpectedMarketCount: "Kalshi didn't have exactly two open fighter markets",
  duplicateFighterNames: 'Both Kalshi markets named the same fighter',
  noOddsEventMatch: 'No odds-provider event matched both fighters and the timing',
  insufficientBookQuotes: 'Fewer than two sportsbooks had a fresh two-way quote',
  invalidKalshiAsk: "Kalshi's own ask price or size wasn't usable",
  noComparableEdge: "Sportsbooks' prices couldn't be compared cleanly",
};

export function FightMarketPage() {
  const query = useQuery({
    queryKey: ['fight-auto-picks'],
    queryFn: getFightAutoPicks,
    staleTime: 120_000,
    refetchInterval: 120_000,
  });
  // In the address, so it survives going back, a reload, or a shared link.
  const [params, setParams] = useSearchParams();
  const range: Range = params.get('range') === 'month' ? 'month' : 'week';
  const setRange = (next: Range) => {
    const nextParams = new URLSearchParams(params);
    if (next === 'week') nextParams.delete('range');
    else nextParams.set('range', next);
    setParams(nextParams, { replace: true });
  };
  const slate = query.data;
  const weekEnd = Date.now() + WEEK_MS;
  const thisWeek =
    slate?.picks.filter((pick) => Date.parse(pick.startsAt) <= weekEnd) ?? [];
  const inRange = range === 'week' ? thisWeek : (slate?.picks ?? []);
  const flags = inRange.filter((pick) => pick.reviewSide !== null).length;
  const groups = groupByCard(inRange);
  const error =
    query.error instanceof ApiClientError
      ? query.error.message
      : 'Fight data is temporarily unavailable.';
  return (
    <div className={styles.page}>
      <div className={styles.heading}>
        <h1>Fight research</h1>
        <RefreshStatus
          updatedAt={query.dataUpdatedAt}
          isFetching={query.isFetching}
          isError={query.isError}
          onRefresh={() => query.refetch()}
        />
      </div>
      <p className={styles.intro}>
        Every open UFC fight on Kalshi over the next month, compared with fight-winner
        odds, to test whether the two markets ever disagree in a useful way. A price-gap
        flag needs two sportsbooks that agree and an estimate at least five points above
        Kalshi&apos;s ask before fees.
      </p>
      <Disclaimer
        summary="Research only — not betting picks."
        text="Read-only research. Price-gap flags are odds-derived comparisons, not predictions, picks, or recommendations to bet or trade. Fees, slippage, limited depth, cancellations and estimation error can erase a displayed price difference."
      />
      {query.isLoading && (
        <>
          <p className="visually-hidden" role="status">
            Checking upcoming fights…
          </p>
          <Skeleton width="60%" height="1.25rem" />
          <Skeleton width="100%" height="10rem" />
        </>
      )}
      {query.isError && !slate && <p role="alert">{error}</p>}
      {slate && (
        <>
          <div className={styles.range} role="group" aria-label="Fights to show">
            <button
              type="button"
              aria-pressed={range === 'week'}
              onClick={() => setRange('week')}
            >
              Next 7 days ({thisWeek.length})
            </button>
            <button
              type="button"
              aria-pressed={range === 'month'}
              onClick={() => setRange('month')}
            >
              Next 30 days ({slate.picks.length})
            </button>
          </div>
          <p className={styles.status}>
            {inRange.length} {inRange.length === 1 ? 'fight' : 'fights'} · {flags}{' '}
            price-gap {flags === 1 ? 'flag' : 'flags'}
          </p>
          {slate.fightEvents === 0 && (
            <p role="status">
              No upcoming UFC fights on Kalshi right now
              {(slate.awaitingResult ?? 0) > 0
                ? ` — the ${slate.awaitingResult} listed ${slate.awaitingResult === 1 ? 'fight has' : 'fights have'} already happened and ${slate.awaitingResult === 1 ? 'is' : 'are'} waiting for Kalshi to settle`
                : ''}
              . Checked {slate.oddsEvents} MMA odds events; picks appear once Kalshi lists
              the next card.
            </p>
          )}
          {slate.fightEvents > 0 && slate.picks.length === 0 && (
            <p role="status">
              Open UFC fights were found, but none had a safe two-feed match with fresh
              odds and both available asks.
            </p>
          )}
          {query.isError && (
            <p role="alert">Refresh failed. Showing the previous slate.</p>
          )}
          {inRange.length === 0 && slate.picks.length > 0 && (
            <p>No matched fights in the next 7 days. Switch to 30 days to see more.</p>
          )}
          {inRange.length > 0 && (
            <p className={styles.legend}>
              <strong>After fee</strong> = the books&apos; estimate minus Kalshi&apos;s
              ask and taker fee, per $1 contract.
            </p>
          )}
          {groups.map((group) => (
            <section key={group.key} aria-labelledby={`card-${group.key}`}>
              <h2 id={`card-${group.key}`} className={styles.cardTitle}>
                {group.label}
              </h2>
              <p className={styles.cardWhen}>{group.when}</p>
              <div className={styles.cards}>
                {group.picks.map((pick) => (
                  <FightCard key={pick.eventTicker} pick={pick} />
                ))}
              </div>
            </section>
          ))}
          {slate.skipped > 0 && (
            <NotShown
              unmatched={slate.unmatched ?? []}
              skipReasons={slate.skipReasons}
              skipped={slate.skipped}
            />
          )}
        </>
      )}
      <TrackRecord />
    </div>
  );
}

interface CardGroup {
  key: string;
  label: string;
  when: string;
  picks: Pick[];
}

function relativeDay(ms: number): string {
  const startOf = (t: number) => {
    const d = new Date(t);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  };
  const days = Math.round((startOf(ms) - startOf(Date.now())) / DAY_MS);
  if (days <= 0) return 'today';
  if (days === 1) return 'tomorrow';
  return `in ${days} days`;
}

/** The last word of a name, lower-cased: "Melissa Croden" -> "croden". */
const surname = (name: string): string => name.trim().split(/\s+/).at(-1)!.toLowerCase();

/**
 * Kalshi sometimes titles a fight by its own matchup ("Croden vs Chandler")
 * rather than by its card ("UFC Fight Night"). Grouping by that would give
 * every fight a heading that repeats the names on its card below it.
 */
function cardLabel(pick: Pick): string {
  if (pick.contenderSeries) return 'Contender Series';
  const label = pick.eventLabel;
  if (!label) return 'UFC';
  const lower = label.toLowerCase();
  const ownMatchup =
    !label.includes(':') &&
    lower.includes(surname(pick.fighterA)) &&
    lower.includes(surname(pick.fighterB));
  return ownMatchup ? 'UFC' : label;
}

/** One group per event card, in start order; picks keep their start order (main event last). */
function groupByCard(picks: readonly Pick[]): CardGroup[] {
  const groups = new Map<string, CardGroup>();
  for (const pick of [...picks].sort(
    (a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt),
  )) {
    const start = new Date(pick.startsAt);
    const day = start.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
    const label = cardLabel(pick);
    const key = `${label}|${start.toDateString()}`;
    const group = groups.get(key);
    if (group) group.picks.push(pick);
    else
      groups.set(key, {
        key: key.replace(/[^a-zA-Z0-9]+/g, '-'),
        label,
        when: `${day} · ${relativeDay(start.getTime())}`,
        picks: [pick],
      });
  }
  return [...groups.values()];
}

/** How the flags and the odds have done on settled fights, from the research log. */
function TrackRecord() {
  const query = useQuery({
    queryKey: ['fight-track-record'],
    queryFn: getFightTrackRecord,
    staleTime: 600_000,
  });
  const s: FightLogSummary | undefined = query.data?.summary;
  if (!s) return null;
  return (
    <section className={styles.track} aria-labelledby="track-heading">
      <h2 id="track-heading">Track record</h2>
      {s.scored === 0 ? (
        <p>
          No fights graded yet. Each matched fight&apos;s last pre-fight prices are logged
          and graded once Kalshi settles it ({s.logged} logged so far).
        </p>
      ) : (
        <>
          <p>
            <strong>
              {(s.flagged ?? 0) === 0 ? (
                'Price-gap flags: none graded yet'
              ) : (
                <>
                  Price-gap flags: {s.flaggedWins ?? 0} of {s.flagged ?? 0} won
                  {s.flaggedNetPerContract != null &&
                    `, ${signedCents(s.flaggedNetPerContract)} per contract after fees`}
                </>
              )}
            </strong>
          </p>
          <p>
            {s.scored} {s.scored === 1 ? 'fight' : 'fights'} graded. Sportsbook favourite
            won {s.bookFavouriteWins} (books expected {s.bookFavouriteExpected.toFixed(1)}
            ). Prediction error, lower is better: books {s.brierBooks?.toFixed(3) ??
              '—'}{' '}
            · Kalshi {s.brierKalshi?.toFixed(3) ?? '—'}.
          </p>
          {s.brierBooksMinusKalshi && (
            <p className={styles.trackNote}>
              Books minus Kalshi, per fight:{' '}
              {signedFixed(s.brierBooksMinusKalshi.mean, 3)} (95% range{' '}
              {signedFixed(s.brierBooksMinusKalshi.low, 3)} to{' '}
              {signedFixed(s.brierBooksMinusKalshi.high, 3)}). Negative means the books
              were closer; a range that includes zero means no clear winner yet.
            </p>
          )}
          {(s.flagged ?? 0) < 30 && (
            <p className={styles.trackNote}>
              Fewer than 30 flags graded — too few to tell skill from luck.
            </p>
          )}
          {s.gapBuckets?.some((b) => b.fights > 0) && <GapTable buckets={s.gapBuckets} />}
          {(s.voided ?? 0) > 0 && (
            <p className={styles.trackNote}>
              {s.voided} {s.voided === 1 ? 'fight' : 'fights'} never settled (cancelled or
              rescheduled) and {s.voided === 1 ? 'is' : 'are'} left out.
            </p>
          )}
        </>
      )}
      {s.closingMove && (
        <p>
          Price moves: on {s.closingMove.fights}{' '}
          {s.closingMove.fights === 1 ? 'fight' : 'fights'} where Kalshi first priced a
          side below the books, its price for that side moved{' '}
          {signedCents(s.closingMove.kalshiMove)} on average by fight time (up on{' '}
          {s.closingMove.movedToward}); the books moved{' '}
          {signedCents(s.closingMove.booksMove)}.
        </p>
      )}
    </section>
  );
}

/** Every graded fight by the larger side's price gap, flagged or not. */
function GapTable({ buckets }: { buckets: FightLogSummary['gapBuckets'] }) {
  const wholeCents = (v: number) =>
    signedCents(Math.round(v * 100) / 100).replace('.0', '');
  return (
    <div className={styles.gapTableWrap}>
      <table className={styles.gapTable}>
        <caption>
          By price gap: book estimate minus Kalshi&apos;s ask on the larger side, buying
          that side at the ask, after fees
        </caption>
        <thead>
          <tr>
            <th scope="col">Gap (points)</th>
            <th scope="col">Fights</th>
            <th scope="col">Won / books expected</th>
            <th scope="col">Per contract</th>
          </tr>
        </thead>
        <tbody>
          {buckets.map((b) => (
            <tr key={b.label}>
              <th scope="row">{b.label}</th>
              <td>{b.fights}</td>
              <td>
                {b.wins} / {b.expectedWins.toFixed(1)}
              </td>
              <td>
                {b.netPerContract ? (
                  <>
                    {signedCents(b.netPerContract.mean)}
                    <span className={styles.gapRange}>
                      {wholeCents(b.netPerContract.low)} to{' '}
                      {wholeCents(b.netPerContract.high)}
                    </span>
                  </>
                ) : (
                  '—'
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NotShown({
  unmatched,
  skipReasons,
  skipped,
}: {
  unmatched: readonly UnmatchedFight[];
  skipReasons: Record<FightSkipReason, number>;
  skipped: number;
}) {
  const sorted = [...unmatched].sort(
    (a, b) => Date.parse(a.fightAt ?? '') - Date.parse(b.fightAt ?? ''),
  );
  return (
    <details className={styles.notShown}>
      <summary>
        {skipped} {skipped === 1 ? 'fight' : 'fights'} on Kalshi not shown
      </summary>
      {sorted.length > 0 ? (
        <ul className={styles.skipReasons}>
          {sorted.map((f) => (
            <li key={f.eventTicker}>
              <strong>{f.matchup}</strong>
              {f.fightAt
                ? ` · ${new Date(f.fightAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
                : ''}{' '}
              — {SKIP_REASON_LABELS[f.reason]}
            </li>
          ))}
        </ul>
      ) : (
        <ul className={styles.skipReasons}>
          {(Object.entries(skipReasons) as [FightSkipReason, number][])
            .filter(([, count]) => count > 0)
            .map(([reason, count]) => (
              <li key={reason}>
                {count} {count === 1 ? 'fight' : 'fights'}: {SKIP_REASON_LABELS[reason]}
              </li>
            ))}
        </ul>
      )}
    </details>
  );
}

function FightCard({ pick }: { pick: Pick }) {
  const reviewName =
    pick.reviewSide === 'A'
      ? pick.fighterA
      : pick.reviewSide === 'B'
        ? pick.fighterB
        : null;
  const sides = [
    { name: pick.fighterA, p: pick.probabilityA, ask: pick.askA, edge: pick.edgeA },
    { name: pick.fighterB, p: pick.probabilityB, ask: pick.askB, edge: pick.edgeB },
  ];
  const gaps = keyGaps(
    pick.fighterA,
    pick.fighterB,
    { wiki: pick.statsA ?? null, ufc: pick.ufcA ?? null },
    { wiki: pick.statsB ?? null, ufc: pick.ufcB ?? null },
  );
  return (
    <article className={styles.card}>
      <div className={styles.cardHeading}>
        <h3>
          {pick.fighterA} vs {pick.fighterB}
        </h3>
        <strong className={reviewName ? styles.review : styles.pass}>
          {reviewName ? `Price gap: ${reviewName}` : 'No gap'}
        </strong>
      </div>
      <table className={styles.prices}>
        <thead>
          <tr>
            <th scope="col">
              <span className="visually-hidden">Fighter</span>
            </th>
            <th scope="col">Books</th>
            <th scope="col">Kalshi</th>
            <th scope="col">After fee</th>
          </tr>
        </thead>
        <tbody>
          {sides.map((side) => {
            const net = netFightEdge(side.edge, side.ask);
            return (
              <tr key={side.name}>
                <th scope="row">{side.name}</th>
                <td>{percent(side.p)}</td>
                <td>{cents(side.ask)}</td>
                <td className={net > 0 ? styles.netUp : undefined}>{signedCents(net)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className={styles.meta}>
        {new Date(pick.startsAt).toLocaleTimeString(undefined, {
          hour: 'numeric',
          minute: '2-digit',
        })}{' '}
        · {pick.bookCount} sportsbooks · books {percent(pick.bookmakerSpread)} apart
      </p>
      {gaps.length > 0 && <p className={styles.gaps}>{gaps.join(' · ')}</p>}
      <FighterStatsPanel
        fighterA={pick.fighterA}
        fighterB={pick.fighterB}
        statsA={pick.statsA ?? null}
        statsB={pick.statsB ?? null}
        ufcA={pick.ufcA ?? null}
        ufcB={pick.ufcB ?? null}
      />
      <a href={pick.marketUrl} target="_blank" rel="noopener noreferrer">
        Check Kalshi market
        <ExternalLinkIcon width={12} height={12} />
      </a>
    </article>
  );
}

const DAY_MS = 24 * 60 * 60_000;

interface Fighter {
  wiki: FighterStats | null;
  ufc: UfcCareerStats | null;
}

const dash = '—';
const pct = (x: number | null | undefined) =>
  x == null ? dash : `${Math.round(x * 100)}%`;
const num = (x: number | null | undefined, digits = 2) =>
  x == null ? dash : x.toFixed(digits);
const cm = (x: number | null | undefined) => (x == null ? dash : `${x} cm`);

function recentResults(f: Fighter) {
  return f.wiki?.recentFights.length
    ? f.wiki.recentFights
    : (f.ufc?.recentUfcFights ?? []);
}

function lastFive(f: Fighter): string {
  const fights = recentResults(f).slice(0, 5);
  return fights.length
    ? fights
        .map((x) => (x.result === 'Win' ? 'W' : x.result === 'Loss' ? 'L' : 'D'))
        .join(' ')
    : dash;
}

function daysSinceLastFight(f: Fighter): number | null {
  const date = recentResults(f).find((x) => x.date)?.date;
  if (!date) return null;
  return Math.floor((Date.now() - Date.parse(`${date}T00:00:00Z`)) / DAY_MS);
}

function sinceLastFight(f: Fighter): string {
  const days = daysSinceLastFight(f);
  if (days === null) return dash;
  return days >= 60 ? `${Math.round(days / 30.4)} months` : `${days} days`;
}

const lastName = (name: string) => name.trim().split(/\s+/).pop() ?? name;

/**
 * The physical and activity gaps big enough to notice at a glance: reach
 * of 5 cm or more, 3+ years of age, a layoff of a year or more. Facts only;
 * whether they matter is what the research log is for.
 */
function keyGaps(nameA: string, nameB: string, a: Fighter, b: Fighter): string[] {
  const out: string[] = [];
  const reachA = a.wiki?.reachCm ?? a.ufc?.reachCm ?? null;
  const reachB = b.wiki?.reachCm ?? b.ufc?.reachCm ?? null;
  if (reachA !== null && reachB !== null && Math.abs(reachA - reachB) >= 5) {
    const longer = reachA > reachB ? nameA : nameB;
    out.push(`${lastName(longer)} +${Math.abs(reachA - reachB)} cm reach`);
  }
  const ageA = ageOn(a.wiki?.birthDate ?? a.ufc?.birthDate ?? null, Date.now());
  const ageB = ageOn(b.wiki?.birthDate ?? b.ufc?.birthDate ?? null, Date.now());
  if (ageA !== null && ageB !== null && Math.abs(ageA - ageB) >= 3) {
    const younger = ageA < ageB ? nameA : nameB;
    out.push(`${lastName(younger)} ${Math.abs(ageA - ageB)} yrs younger`);
  }
  for (const [name, f] of [
    [nameA, a],
    [nameB, b],
  ] as const) {
    const days = daysSinceLastFight(f);
    if (days !== null && days >= 365) {
      out.push(`${lastName(name)} out ${Math.round(days / 30.4)} months`);
    }
  }
  return out;
}

const ROWS: [string, (f: Fighter) => string][] = [
  [
    'Pro record',
    (f) =>
      f.wiki?.record
        ? `${f.wiki.record.wins}-${f.wiki.record.losses}${f.wiki.record.draws ? `-${f.wiki.record.draws}` : ''}`
        : dash,
  ],
  [
    'UFC record',
    (f) =>
      f.ufc
        ? `${f.ufc.ufcRecord.wins}-${f.ufc.ufcRecord.losses}${f.ufc.ufcRecord.draws ? `-${f.ufc.ufcRecord.draws}` : ''}`
        : dash,
  ],
  [
    'Strikes landed / absorbed per min',
    (f) => (f.ufc ? `${num(f.ufc.slpm)} / ${num(f.ufc.sapm)}` : dash),
  ],
  [
    'Striking accuracy / defense',
    (f) => (f.ufc ? `${pct(f.ufc.strikeAccuracy)} / ${pct(f.ufc.strikeDefense)}` : dash),
  ],
  ['Takedowns per 15 min', (f) => num(f.ufc?.tdPer15)],
  [
    'Takedown accuracy / defense',
    (f) => (f.ufc ? `${pct(f.ufc.tdAccuracy)} / ${pct(f.ufc.tdDefense)}` : dash),
  ],
  ['Sub attempts per 15 min', (f) => num(f.ufc?.subAttemptsPer15)],
  [
    'Wins KO/Sub/Dec',
    (f) =>
      f.wiki?.winsBy
        ? `${f.wiki.winsBy.ko}/${f.wiki.winsBy.sub}/${f.wiki.winsBy.dec}`
        : dash,
  ],
  ['Last 5 (newest first)', lastFive],
  ['Since last fight', sinceLastFight],
  [
    'Age',
    (f) =>
      String(ageOn(f.wiki?.birthDate ?? f.ufc?.birthDate ?? null, Date.now()) ?? dash),
  ],
  ['Height', (f) => cm(f.wiki?.heightCm ?? f.ufc?.heightCm)],
  ['Reach', (f) => cm(f.wiki?.reachCm ?? f.ufc?.reachCm)],
  ['Stance', (f) => f.wiki?.stance ?? f.ufc?.stance ?? dash],
];

/** Side-by-side fighter facts, collapsed by default so the card stays short. */
function FighterStatsPanel({
  fighterA,
  fighterB,
  statsA,
  statsB,
  ufcA,
  ufcB,
}: {
  fighterA: string;
  fighterB: string;
  statsA: FighterStats | null;
  statsB: FighterStats | null;
  ufcA: UfcCareerStats | null;
  ufcB: UfcCareerStats | null;
}) {
  const a: Fighter = { wiki: statsA, ufc: ufcA };
  const b: Fighter = { wiki: statsB, ufc: ufcB };
  if (!statsA && !statsB && !ufcA && !ufcB) {
    return <p className={styles.statsMissing}>No stats found for these fighters yet.</p>;
  }
  // Hide rows neither fighter has, so a debut fighter's card isn't a wall of dashes.
  const rows = ROWS.map(([label, value]) => [label, value(a), value(b)] as const).filter(
    ([, x, y]) => x !== dash || y !== dash,
  );
  const wikiLinks = [statsA, statsB].filter((s): s is FighterStats => s !== null);
  const ufcLinks = [ufcA, ufcB].filter(
    (s): s is UfcCareerStats & { ufcStatsUrl: string } =>
      s !== null && s.ufcStatsUrl !== null,
  );
  return (
    <details className={styles.stats}>
      <summary>Fighter stats</summary>
      <table>
        <thead>
          <tr>
            <th scope="col">
              <span className="visually-hidden">Stat</span>
            </th>
            <th scope="col">{fighterA}</th>
            <th scope="col">{fighterB}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([label, x, y]) => (
            <tr key={label}>
              <th scope="row">{label}</th>
              <td>{x}</td>
              <td>{y}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className={styles.statsSource}>
        UFC numbers: UFC Stats via{' '}
        <a href={UFC_STATS_SOURCE} target="_blank" rel="noopener noreferrer">
          scrape_ufc_stats
        </a>
        {ufcLinks.map((s) => (
          <span key={s.ufcStatsUrl}>
            {' · '}
            <a href={s.ufcStatsUrl} target="_blank" rel="noopener noreferrer">
              {s.name}
            </a>
          </span>
        ))}
        {wikiLinks.length > 0 && <>. Pro record: Wikipedia (CC BY-SA)</>}
        {wikiLinks.map((s) => (
          <span key={s.wikiUrl}>
            {' · '}
            <a href={s.wikiUrl} target="_blank" rel="noopener noreferrer">
              {s.name}
            </a>
          </span>
        ))}
      </p>
    </details>
  );
}
